import type { Capability } from '@clawharbor/manifest';
import { html } from '../html.js';
import type { VNode } from '../html.js';

/**
 * The human's half of the surface.
 *
 * The agent gets the capability's `inputSchema` as a JSON Schema; the page gets
 * the same schema rendered as controls. One declaration, two callers — which is
 * what makes "the agent filters, the human chooses" literally true rather than
 * two implementations that drift.
 *
 * If `document.modelContext` is absent this form IS the product: a normal,
 * working website with no agent involved anywhere.
 */

interface JsonSchemaNode {
  type?: string | string[];
  title?: string;
  description?: string;
  format?: string;
  enum?: unknown[];
  default?: unknown;
  items?: JsonSchemaNode;
  minimum?: number;
  maximum?: number;
  examples?: unknown[];
  properties?: Record<string, JsonSchemaNode>;
  required?: string[];
}

export type FormValues = Record<string, unknown>;

function typeOf(node: JsonSchemaNode): string {
  const t = node.type;
  if (Array.isArray(t)) return t.find((x) => x !== 'null') ?? 'string';
  return t ?? 'string';
}

function labelFor(name: string, node: JsonSchemaNode): string {
  return node.title ?? name.replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Strips empty strings and empty arrays so an omitted filter is truly omitted. */
export function compactValues(values: FormValues): FormValues {
  const out: FormValues = {};
  for (const [k, v] of Object.entries(values)) {
    if (v == null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out;
}

export function defaultValues(cap: Capability): FormValues {
  const schema = cap.inputSchema as JsonSchemaNode;
  const props = schema.properties ?? {};
  const out: FormValues = {};
  for (const [name, node] of Object.entries(props)) {
    if (node.default !== undefined) out[name] = node.default;
  }
  return out;
}

function inputTypeFor(node: JsonSchemaNode): string {
  if (node.format === 'date') return 'date';
  if (node.format === 'time') return 'time';
  if (typeOf(node) === 'number' || typeOf(node) === 'integer') return 'number';
  return 'text';
}

function fieldNode(
  name: string,
  node: JsonSchemaNode,
  required: boolean,
  values: FormValues,
  onChange: (name: string, value: unknown) => void,
): VNode {
  const id = `ch-f-${name}`;
  const label = labelFor(name, node);
  const type = typeOf(node);
  const current = values[name];

  const hint = node.description
    ? html`<span class="chw-hint" id=${`${id}-hint`}>${node.description}</span>`
    : null;

  // Enumerated scalar -> select. Cheaper for a human than free text, and it
  // makes the site's own vocabulary discoverable.
  if (Array.isArray(node.enum) && node.enum.length) {
    return html`
      <div class="chw-field">
        <label for=${id}>${label}${required ? ' *' : ''}</label>
        <select
          id=${id}
          value=${current == null ? '' : String(current)}
          onChange=${(e: Event) => onChange(name, (e.currentTarget as HTMLSelectElement).value)}
        >
          <option value="">${required ? 'Choose…' : 'Any'}</option>
          ${node.enum.map((v) => html`<option key=${String(v)} value=${String(v)}>${String(v)}</option>`)}
        </select>
        ${hint}
      </div>
    `;
  }

  if (type === 'boolean') {
    return html`
      <div class="chw-field">
        <label class="chw-check" for=${id}>
          <input
            id=${id}
            type="checkbox"
            checked=${current === true}
            onChange=${(e: Event) => onChange(name, (e.currentTarget as HTMLInputElement).checked)}
          />
          ${label}
        </label>
        ${hint}
      </div>
    `;
  }

  if (type === 'array') {
    const item = node.items ?? {};
    const options = Array.isArray(item.enum) ? item.enum.map(String) : [];
    const selected = new Set((Array.isArray(current) ? current : []).map(String));

    // Free-form arrays get a comma-separated text box; enumerated ones get
    // checkboxes, which is the only shape a phone user can actually operate.
    if (!options.length) {
      return html`
        <div class="chw-field">
          <label for=${id}>${label}</label>
          <input
            id=${id}
            type="text"
            placeholder="comma separated"
            value=${(Array.isArray(current) ? current : []).join(', ')}
            onInput=${(e: Event) =>
              onChange(
                name,
                (e.currentTarget as HTMLInputElement).value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              )}
          />
          ${hint}
        </div>
      `;
    }

    return html`
      <fieldset class="chw-field" style="border:0;padding:0;margin:0">
        <legend style="font-size:var(--ch-size-xs,13px);font-weight:700;padding:0">${label}</legend>
        <div class="chw-checks">
          ${options.map(
            (opt) => html`
              <label class="chw-check" key=${opt}>
                <input
                  type="checkbox"
                  checked=${selected.has(opt)}
                  onChange=${(e: Event) => {
                    const next = new Set(selected);
                    if ((e.currentTarget as HTMLInputElement).checked) next.add(opt);
                    else next.delete(opt);
                    onChange(name, [...next]);
                  }}
                />
                ${opt}
              </label>
            `,
          )}
        </div>
        ${hint}
      </fieldset>
    `;
  }

  const inputType = inputTypeFor(node);
  return html`
    <div class="chw-field">
      <label for=${id}>${label}${required ? ' *' : ''}</label>
      <input
        id=${id}
        type=${inputType}
        value=${current == null ? '' : String(current)}
        placeholder=${node.examples && node.examples.length ? String(node.examples[0]) : ''}
        aria-describedby=${node.description ? `${id}-hint` : undefined}
        onInput=${(e: Event) => {
          const raw = (e.currentTarget as HTMLInputElement).value;
          onChange(name, inputType === 'number' ? (raw === '' ? '' : Number(raw)) : raw);
        }}
      />
      ${hint}
    </div>
  `;
}

export interface ControlsProps {
  capability: Capability;
  values: FormValues;
  pending: boolean;
  onChange: (name: string, value: unknown) => void;
  onSubmit: () => void;
  onReset: () => void;
}

export function Controls(props: ControlsProps): VNode {
  const schema = props.capability.inputSchema as JsonSchemaNode;
  const props_ = schema.properties ?? {};
  const required = new Set(schema.required ?? []);
  const entries = Object.entries(props_);

  const verb =
    props.capability.kind === 'stage'
      ? 'Stage selection'
      : props.capability.kind === 'navigate'
        ? 'Open'
        : 'Search';

  return html`
    <form
      class="chw-panel ch-root"
      onSubmit=${(e: Event) => {
        e.preventDefault();
        props.onSubmit();
      }}
    >
      <p class="chw-panel-desc">${props.capability.description}</p>
      ${entries.length
        ? html`<div class="chw-fields">
            ${entries.map(([name, node]) =>
              fieldNode(name, node, required.has(name), props.values, props.onChange),
            )}
          </div>`
        : html`<p class="chw-panel-desc">This action takes no parameters.</p>`}
      <div class="chw-actions">
        <button class="ch-btn" type="submit" disabled=${props.pending}>
          ${props.pending ? 'Working…' : verb}
        </button>
        <button class="ch-btn ch-btn--ghost" type="button" onClick=${props.onReset}>Reset</button>
      </div>
    </form>
  `;
}
